/**
 * EventSource hook that consumes a book's generation stream.
 *
 * One `EventSource` per book multiplexes three documented event types:
 *   - `page_text`    — a page's text (or an incremental delta of it)
 *   - `image_ready`  — the finished illustration URL for a page
 *   - `book_complete`— the fully materialised Book, used to precache for offline
 *
 * The hook builds an ordered page list as events arrive so the reader can show
 * page 1 while later pages are still streaming. It does not throw on transient
 * disconnects — EventSource auto-reconnects — but surfaces a terminal error if
 * the stream cannot be opened.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { books } from "@/lib/api";
import type { Book, BookPage } from "@/lib/api";
import { cacheBook } from "@/offline/bookCache";

export type StreamStatus = "connecting" | "streaming" | "complete" | "error";

/** A page as it materialises during streaming. */
export interface StreamingPage {
  pageNumber: number;
  text: string;
  /** Final aspect ratio (width / height) so the placeholder reserves space. */
  aspectRatio: number;
  image: { url: string; width: number; height: number } | null;
  textComplete: boolean;
  /** Whether this page is meant to be illustrated under the level-scaled cadence. */
  expectsImage: boolean;
  /** True when an expected illustration failed to generate (after retries). */
  imageFailed: boolean;
}

/** `page_text` event payload (snake_case, as emitted by the FastAPI pipeline). */
interface PageTextEvent {
  book_id: string;
  page_number: number;
  text: string;
  expects_image?: boolean;
}

/** `image_ready` event payload (snake_case). */
interface ImageReadyEvent {
  book_id: string;
  page_number: number;
  image_path: string;
  image_sha256: string;
}

/** `image_failed` event payload (snake_case). */
interface ImageFailedEvent {
  book_id: string;
  page_number: number;
}

/** `book_complete` event payload — a generation summary, not the full Book. */
interface BookCompleteSummary {
  book_id: string;
}

/** `rejected` event payload for a safe terminal failure. */
interface RejectedEvent {
  code: string;
  message: string;
}

/** Generated illustrations come from the 1024×1024 Flux-Schnell workflow. */
const IMAGE_SIDE = 1024;

/**
 * Live EventSources keyed by stream URL.
 *
 * React 18+ StrictMode mounts effects twice in development (mount → cleanup →
 * mount). The SSE token is one-shot on the server, so naively reopening the
 * EventSource on the second mount hits an already-consumed token and 404s,
 * surfacing a spurious "storyteller is napping" error. We stash the live source
 * here and defer its close on cleanup; a near-instant remount reclaims the same
 * connection instead of opening (and failing) a second one.
 */
const liveSources = new Map<
  string,
  { source: EventSource; closeTimer?: ReturnType<typeof setTimeout> }
>();

export interface UseBookStreamResult {
  status: StreamStatus;
  pages: StreamingPage[];
  totalPages: number | null;
  book: Book | null;
  error: string | null;
}

function upsertPage(
  pages: StreamingPage[],
  pageNumber: number,
  mutate: (page: StreamingPage) => StreamingPage,
): StreamingPage[] {
  const index = pages.findIndex((p) => p.pageNumber === pageNumber);
  const base: StreamingPage =
    index >= 0
      ? pages[index]!
      : {
          pageNumber,
          text: "",
          aspectRatio: 1,
          image: null,
          textComplete: false,
          expectsImage: true,
          imageFailed: false,
        };
  const next = mutate(base);
  const copy = index >= 0 ? [...pages] : [...pages, next];
  if (index >= 0) copy[index] = next;
  copy.sort((a, b) => a.pageNumber - b.pageNumber);
  return copy;
}

function toBookPage(page: StreamingPage): BookPage {
  return {
    pageNumber: page.pageNumber,
    text: page.text,
    imageUrl: page.image?.url ?? null,
    imageWidth: page.image?.width ?? 0,
    imageHeight: page.image?.height ?? 0,
    expectsImage: page.expectsImage,
  };
}

/**
 * Subscribe to a book's SSE generation stream.
 *
 * @param streamUrl The EventSource URL from `books.create` / `books.streamUrl`,
 *   or `null` to stay idle (e.g. when re-reading an already-cached book).
 */
export function useBookStream(streamUrl: string | null): UseBookStreamResult {
  const [status, setStatus] = useState<StreamStatus>(
    streamUrl ? "connecting" : "complete",
  );
  const [pages, setPages] = useState<StreamingPage[]>([]);
  const [totalPages, setTotalPages] = useState<number | null>(null);
  const [book, setBook] = useState<Book | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pagesRef = useRef<StreamingPage[]>([]);

  const apply = useCallback(
    (updater: (prev: StreamingPage[]) => StreamingPage[]) => {
      setPages((prev) => {
        const next = updater(prev);
        pagesRef.current = next;
        return next;
      });
    },
    [],
  );

  useEffect(() => {
    if (!streamUrl) return;

    setStatus("connecting");
    setPages([]);
    setTotalPages(null);
    setBook(null);
    setError(null);
    pagesRef.current = [];

    // Reclaim an existing connection if a StrictMode remount deferred its close;
    // otherwise open a fresh one. Either way the one-shot token is used once.
    const existing = liveSources.get(streamUrl);
    if (existing?.closeTimer) {
      clearTimeout(existing.closeTimer);
      delete existing.closeTimer;
    }
    const source = existing?.source ?? new EventSource(streamUrl, { withCredentials: true });
    if (!existing) {
      liveSources.set(streamUrl, { source });
    }
    if (source.readyState === EventSource.OPEN) setStatus("streaming");

    const forget = () => {
      source.close();
      if (liveSources.get(streamUrl)?.source === source) {
        liveSources.delete(streamUrl);
      }
    };

    const onOpen = () => setStatus("streaming");

    const onPageText = (event: Event) => {
      const data = JSON.parse((event as MessageEvent).data) as PageTextEvent;
      apply((prev) =>
        upsertPage(prev, data.page_number, (page) => ({
          ...page,
          text: data.text,
          textComplete: true,
          expectsImage: data.expects_image ?? page.expectsImage,
        })),
      );
    };

    const onImageReady = (event: Event) => {
      const data = JSON.parse((event as MessageEvent).data) as ImageReadyEvent;
      apply((prev) =>
        upsertPage(prev, data.page_number, (page) => ({
          ...page,
          aspectRatio: 1,
          image: { url: data.image_path, width: IMAGE_SIDE, height: IMAGE_SIDE },
          imageFailed: false,
        })),
      );
    };

    const onImageFailed = (event: Event) => {
      const data = JSON.parse((event as MessageEvent).data) as ImageFailedEvent;
      apply((prev) =>
        upsertPage(prev, data.page_number, (page) => ({
          ...page,
          imageFailed: true,
        })),
      );
    };

    const onComplete = (event: Event) => {
      const summary = JSON.parse((event as MessageEvent).data) as BookCompleteSummary;
      forget();
      // The summary carries no pages; fetch the materialised book so the reader
      // and the offline cache get the authoritative content.
      void books
        .get(summary.book_id)
        .then((completed) => {
          setBook(completed);
          setStatus("complete");
          void cacheBook(completed);
        })
        .catch(() => {
          setStatus("error");
          setError((current) => current ?? "The finished story could not be loaded.");
        });
    };

    const onRejected = (event: Event) => {
      const data = JSON.parse((event as MessageEvent).data) as RejectedEvent;
      forget();
      setStatus("error");
      setError(data.message);
    };

    const onError = () => {
      // EventSource retries automatically while CONNECTING; only treat a fully
      // closed stream as terminal.
      if (source.readyState === EventSource.CLOSED) {
        setStatus((current) => (current === "complete" ? current : "error"));
        setError((current) => current ?? "The story stream was interrupted.");
      }
    };

    source.addEventListener("open", onOpen);
    source.addEventListener("page_text", onPageText);
    source.addEventListener("image_ready", onImageReady);
    source.addEventListener("image_failed", onImageFailed);
    source.addEventListener("book_complete", onComplete);
    source.addEventListener("rejected", onRejected);
    source.addEventListener("error", onError);

    return () => {
      source.removeEventListener("open", onOpen);
      source.removeEventListener("page_text", onPageText);
      source.removeEventListener("image_ready", onImageReady);
      source.removeEventListener("image_failed", onImageFailed);
      source.removeEventListener("book_complete", onComplete);
      source.removeEventListener("rejected", onRejected);
      source.removeEventListener("error", onError);
      // Defer the close so an immediate StrictMode remount can reclaim it; a real
      // unmount (navigating away) lets the timer fire and close the stream.
      const entry = liveSources.get(streamUrl);
      if (entry?.source === source) {
        entry.closeTimer = setTimeout(() => {
          source.close();
          if (liveSources.get(streamUrl)?.source === source) {
            liveSources.delete(streamUrl);
          }
        }, 200);
      } else {
        source.close();
      }
    };
  }, [streamUrl, apply]);

  return { status, pages, totalPages, book, error };
}

export { toBookPage };
